import type { ReactElement, ReactNode } from 'react';
import { Tooltip as RadixTooltip } from 'radix-ui';

export interface TooltipProps {
  content: ReactNode;
  children: ReactElement;
}

/**
 * Thin wrapper around `radix-ui`'s Tooltip primitive (authored the same way
 * as `Badge` - small, typed, no internal state beyond what Radix manages).
 * Radix's Trigger natively shows `content` on both pointer hover and
 * keyboard focus, which is what makes this accessible without extra wiring -
 * required per mnr-standards' behavioural checklist (genuine keyboard-focus
 * accessibility, not just mouse hover).
 *
 * `delayDuration={0}` - no design source specifies a hover delay for this
 * tier-3 admin addition, and an instant show keeps a fast-moving admin
 * listing responsive.
 */
export function Tooltip({ content, children }: TooltipProps) {
  return (
    <RadixTooltip.Provider delayDuration={0}>
      <RadixTooltip.Root>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            sideOffset={6}
            className="z-50 max-w-xs rounded-lg bg-navy px-3 py-2 text-xs text-white shadow-lg"
          >
            {content}
            <RadixTooltip.Arrow className="fill-navy" />
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}
